import { useState, useEffect, useMemo } from "react";
import ChangePeriodRow from "../../../components/common/ChangePeriodRow";
import {
	useCategoriesQuery,
	useTransactionsQuery,
	useBudgetsQuery,
	useWalletsQuery,
} from "../../../services/queryHooks";
import { Loading } from "../../../components/common/Loading";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { NorthEast, TrendingUp, Savings, SouthEast } from "@mui/icons-material";
import BudgetSummaryTile from "./BudgetSummaryTile";
import { Box } from "@mui/material";
import BudgetGroup from "./BudgetGroup";
import { getGroupedBudgetsByType } from "./getGroupedBudgetsByType";
import InvestmentBudgetGroup from "./InvestmentBudgetGroup";

export default function Budget() {
	const theme = useTheme();
	const { t } = useTranslation();
	const [period, setPeriod] = useState(() => {
		const d = new Date();
		d.setUTCHours(0, 0, 0, 0);
		return d.toISOString();
	});
	const [periodType, setPeriodType] = useState("Monthly");
	const [periodRange, setPeriodRange] = useState({
		startDate: "",
		endDate: "",
	});

	useEffect(() => {
		const date = new Date(period);
		let startDate, endDate;

		if (periodType === "Monthly") {
			const year = date.getUTCFullYear();
			const month = date.getUTCMonth();

			startDate = new Date(Date.UTC(year, month, 1));
			endDate = new Date(Date.UTC(year, month + 1, 0));
		} else if (periodType === "Yearly") {
			const year = date.getUTCFullYear();

			startDate = new Date(Date.UTC(year, 0, 1));
			endDate = new Date(Date.UTC(year, 11, 31));
		}

		setPeriodRange({
			startDate,
			endDate,
		});
	}, [period, periodType]);

	const { data: categories, isPending: categoriesLoading } =
		useCategoriesQuery();
	const { data: transactions = [], isPending: transactionsLoading } =
		useTransactionsQuery(periodRange.startDate, periodRange.endDate);
	const { data: budgets, isPending: budgetsLoading } =
		useBudgetsQuery(period);
	const { data: wallets, isPending: walletsLoading } = useWalletsQuery();

	const groupedBudgetsByType = useMemo(() => {
		if (!categories || !budgets || !wallets)
			return { income: [], expense: [], investment: [] };

		return getGroupedBudgetsByType({
			categories,
			budgets,
			transactions,
			wallets,
		});
	}, [categories, budgets, transactions, wallets]);

	const incomeBudget = groupedBudgetsByType.income.reduce(
		(sum, group) => sum + group.totalBudget,
		0,
	);
	const expenseBudget = groupedBudgetsByType.expense.reduce(
		(sum, group) => sum + group.totalBudget,
		0,
	);
	const investmentBudget = groupedBudgetsByType.investment.reduce(
		(sum, group) => sum + group.totalBudget,
		0,
	);
	const leftToBudget = incomeBudget - expenseBudget - investmentBudget;

	const transactionsByPeriodAndType = (type) =>
		transactions.filter((transaction) => {
			if (type === "investment-debt-payoff-auto") {
				return transaction.type === type;
			}
			return (
				transaction.type === type &&
				transaction.includeInAnalysis === true &&
				transaction.investmentDebtPayoff === ""
			);
		});

	const transactionsSumByType = (type) =>
		transactionsByPeriodAndType(type).reduce(
			(sum, transaction) =>
				sum +
				(transaction.exchangeRate !== 1
					? transaction.amount * transaction.exchangeRate
					: transaction.amount),
			0,
		);

	const summaryInformation = [
		{
			title: t("incomes"),
			budget: incomeBudget,
			actual: transactionsSumByType("income"),
			icon: <NorthEast />,
			color: theme.palette.values.positive,
		},
		{
			title: t("expenses"),
			budget: expenseBudget,
			actual: transactionsSumByType("expense"),
			icon: <SouthEast />,
			color: theme.palette.values.negative,
		},
		{
			title: t("investments"),
			budget: investmentBudget,
			actual: transactionsSumByType("investment-debt-payoff-auto"),
			icon: <TrendingUp />,
			color: theme.palette.values.investment,
		},
		{
			title: t("leftToBudget"),
			budget: leftToBudget,
			actual: null,
			icon: <Savings />,
			color: theme.palette.text.primary,
		},
	];

	const summaryInfoRowStyles = {
		display: "flex",
		justifyContent: "flex-start",
		flexWrap: "wrap",
		m: 2,
		gap: 2,
	};

	return (
		<>
			{(categoriesLoading ||
				transactionsLoading ||
				budgetsLoading ||
				walletsLoading) && <Loading />}
			<ChangePeriodRow
				period={period}
				setPeriod={setPeriod}
				periodType={periodType}
				setPeriodType={setPeriodType}
				periodTypeOptions={["Monthly"]}
			/>
			<Box sx={summaryInfoRowStyles}>
				{summaryInformation.map((i) => (
					<BudgetSummaryTile key={i.title} {...i} />
				))}
			</Box>
			<Box
				sx={{
					m: 2,
					display: "flex",
					alignItems: "flex-start",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<BudgetGroup
					type="income"
					title={t("incomes")}
					period={period}
					groupedBudgets={groupedBudgetsByType.income}
					icon={<NorthEast />}
					color={theme.palette.values.positive}
				/>
				<BudgetGroup
					type="expense"
					title={t("expenses")}
					period={period}
					groupedBudgets={groupedBudgetsByType.expense}
					icon={<SouthEast />}
					color={theme.palette.values.negative}
				/>
				<InvestmentBudgetGroup
					investment={groupedBudgetsByType.investment}
					period={period}
					color={theme.palette.values.investment}
				/>
			</Box>
		</>
	);
}
